import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { Goal, GoalType } from '@/hooks/use-goals';
import { PiggyBank, Shield, TrendingDown } from 'lucide-react';

const formSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  type: z.enum(['simple_savings', 'emergency_fund', 'category_limit']),
  target_amount: z.coerce.number().min(0),
  current_amount: z.coerce.number().min(0),
  target_date: z.string().optional(),
  category_id: z.string().optional(),
  auto_transfer: z.boolean(),
  auto_transfer_type: z.enum(['fixed', 'percentage']),
  auto_transfer_amount: z.coerce.number().min(0).optional(),
  auto_transfer_percentage: z.coerce.number().min(0).max(100).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface GoalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal?: Goal | null;
  onSave: (data: Partial<Goal>) => void;
}

const TYPE_OPTIONS = [
  {
    value: 'simple_savings',
    label: 'Poupança Simples',
    description: 'Meta livre para qualquer objetivo',
    icon: PiggyBank,
  },
  {
    value: 'emergency_fund',
    label: 'Reserva de Emergência',
    description: 'Meta baseada em meses de despesas',
    icon: Shield,
  },
  {
    value: 'category_limit',
    label: 'Limite por Categoria',
    description: 'Controle de gastos em uma categoria',
    icon: TrendingDown,
  },
];

export function GoalModal({ open, onOpenChange, goal, onSave }: GoalModalProps) {
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: 'simple_savings',
      target_amount: 0,
      current_amount: 0,
      target_date: '',
      category_id: '',
      auto_transfer: false,
      auto_transfer_type: 'fixed',
      auto_transfer_amount: 0,
      auto_transfer_percentage: 0,
    },
  });

  const goalType = form.watch('type');
  const autoTransfer = form.watch('auto_transfer');
  const autoTransferType = form.watch('auto_transfer_type');

  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');
      setCategories(data || []);
    };

    if (open) fetchCategories();
  }, [open]);

  useEffect(() => {
    if (goal) {
      form.reset({
        name: goal.name,
        type: goal.type,
        target_amount: goal.target_amount,
        current_amount: goal.current_amount,
        target_date: goal.target_date || '',
        category_id: goal.category_id || '',
        auto_transfer: goal.auto_transfer,
        auto_transfer_type: goal.auto_transfer_percentage ? 'percentage' : 'fixed',
        auto_transfer_amount: goal.auto_transfer_amount || 0,
        auto_transfer_percentage: goal.auto_transfer_percentage || 0,
      });
    } else {
      form.reset({
        name: '',
        type: 'simple_savings',
        target_amount: 0,
        current_amount: 0,
        target_date: '',
        category_id: '',
        auto_transfer: false,
        auto_transfer_type: 'fixed',
        auto_transfer_amount: 0,
        auto_transfer_percentage: 0,
      });
    }
  }, [goal, open]);

  const handleSubmit = (values: FormValues) => {
    onSave({
      name: values.name,
      type: values.type as GoalType,
      target_amount: values.target_amount,
      current_amount: values.current_amount,
      target_date: values.target_date || null,
      category_id: values.category_id || null,
      auto_transfer: values.auto_transfer,
      auto_transfer_amount: values.auto_transfer_type === 'fixed'
        ? values.auto_transfer_amount
        : null,
      auto_transfer_percentage: values.auto_transfer_type === 'percentage'
        ? values.auto_transfer_percentage
        : null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{goal ? 'Editar Cofrinho' : 'Novo Cofrinho'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Viagem, Reserva, IPVA" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo</FormLabel>
                  <div className="grid gap-2">
                    {TYPE_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      const isSelected = field.value === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => field.onChange(option.value)}
                          className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                            isSelected
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{option.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {option.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {goalType === 'category_limit' && (
              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma categoria..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="target_amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Meta (R$)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="current_amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Saldo Atual (R$)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="target_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data Alvo</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormDescription>
                    Opcional: quando você quer atingir essa meta
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="auto_transfer"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel>Auto-transferência</FormLabel>
                    <FormDescription>
                      Transferir automaticamente ao receber salário
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            {autoTransfer && (
              <div className="space-y-3 pl-4 border-l-2 border-primary/30">
                <FormField
                  control={form.control}
                  name="auto_transfer_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Transferência</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="fixed">Valor Fixo</SelectItem>
                          <SelectItem value="percentage">Percentual do Salário</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {autoTransferType === 'fixed' ? (
                  <FormField
                    control={form.control}
                    name="auto_transfer_amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valor (R$)</FormLabel>
                        <FormControl>
                          <Input type="number" step="0.01" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="auto_transfer_percentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Percentual (%)</FormLabel>
                        <FormControl>
                          <Input type="number" min="0" max="100" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit">{goal ? 'Salvar' : 'Criar'}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
